"use client";
import React, { useRef, useEffect } from "react";

const plans = [
  {
    name: "Quick Clarity",
    duration: "20 min",
    price: "₹1,600",
    highlight: false,
  },
  {
    name: "Deep Insight",
    duration: "40 min",
    price: "₹2,700",
    highlight: true,
    badge: "Most Popular",
  },
  {
    name: "Transformation Session",
    duration: "60 min",
    price: "₹3,600",
    highlight: false,
  },
];

const PricingSection: React.FC = () => {
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove("card-hidden");
            entry.target.classList.add("card-reveal");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    cardsRef.current.forEach((card) => {
      if (card) {
        card.classList.add("card-hidden");
        observer.observe(card);
      }
    });

    let rafId: number;
    const handleParallax = () => {
      if (!parallaxRef.current) return;
      const rect = parallaxRef.current.getBoundingClientRect();
      const offset = rect.top * 0.4;
      parallaxRef.current.style.transform = `translateY(${offset * 0.08}px)`;
      rafId = requestAnimationFrame(handleParallax);
    };
    rafId = requestAnimationFrame(handleParallax);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section id="pricing" className="relative py-20 md:py-28 px-6 bg-secondary/40">
      <div ref={parallaxRef} className="max-w-5xl mx-auto" style={{ willChange: "transform" }}>
        <div className="text-center mb-14">
          <span className="section-label">Investment</span>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-foreground mt-4">
            Pricing
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {plans.map((p, i) => (
            <div
              key={i}
              ref={(el) => { cardsRef.current[i] = el; }}
              className={`pricing-card p-8 flex flex-col items-center text-center gap-4 ${
                p.highlight ? "pricing-card-highlight md:scale-105" : ""
              }`}
            >
              {p.badge && (
                <span className="inline-block px-4 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold tracking-wider uppercase">
                  {p.badge}
                </span>
              )}
              <h3 className="font-display text-2xl text-foreground">{p.name}</h3>
              <p className="text-foreground-muted text-sm font-body">{p.duration}</p>
              <p className="font-display text-4xl text-accent font-bold">{p.price}</p>
              <a
                href="https://wa.me/919993297909?text=Hi%2C%20I%20want%20to%20book%20a%20tarot%20reading%20session."
                target="_blank"
                rel="noopener noreferrer"
                className={`mt-auto w-full py-3 rounded-full text-sm font-semibold text-center ${
                  p.highlight
                    ? "btn-gold"
                    : "btn-outline-gold"
                }`}
              >
                Book Now
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
